import { useEffect, useMemo, useState } from "react";
import "./DesktopLogin.css";
import "./DesktopRegister.css";
import { authApi, setAuthToken } from "../../../api";
import { useLang } from "../../../i18n/LangContext";

const FALLBACK = {
  eyebrow: "REGISTER",
  title: "Create your account",
  subtitle: "Share your business details so we can onboard you faster.",
  submit: "Register",
  success: "Registered successfully.",
  missingFields: "Please fill the required fields.",
  genericError: "Unable to register. Please try again.",
  emailLabel: "Email",
  emailPlaceholder: "you@example.com",
  passwordLabel: "Password",
  passwordPlaceholder: "Enter a password",
  companyLabel: "Company",
  companyPlaceholder: "Company name",
  contactLabel: "Contact person",
  contactPlaceholder: "Full name",
  countryLabel: "Country",
  countryPlaceholder: "Select country",
  commPlatformLabel: "Communication platform",
  commHandleLabel: "Handle",
  commHandlePlaceholder: "Username",
  appLabel: "App/Website link",
  appPlaceholder: "Link",
  dauLabel: "Daily Active Users",
  dauPlaceholder: "Active users",
  revenueLabel: "Monthly Revenue",
  revenuePlaceholder: "Revenue",
  messageLabel: "Message",
  messagePlaceholder: "Describe your business",
  languageLabel: "Language",
};
const DEFAULT_PLATFORMS = [
  { label: "Skype", value: "skype" },
  { label: "Slack", value: "slack" },
  { label: "Telegram", value: "telegram" },
  { label: "WhatsApp", value: "whatsapp" },
];

const LANG_OPTIONS = [
  { label: "English", value: "en" },
  { label: "Bulgarian", value: "bg" },
];

export default function DesktopRegister({ open, onClose = () => {}, onSuccess = () => {} }) {
  const { t } = useLang();
  const copy = useMemo(() => t.registerModal || FALLBACK, [t.registerModal]);
  const platformOptions = useMemo(
    () => copy.communicationOptions || DEFAULT_PLATFORMS,
    [copy.communicationOptions]
  );

  const [values, setValues] = useState({
    email: "",
    password: "",
    companyName: "",
    contactPerson: "",
    country: "",
    communicationPlatform: platformOptions?.[0]?.value || "",
    communicationHandle: "",
    appWebsiteLink: "",
    dailyActiveUsers: "",
    monthlyRevenue: "",
    message: "",
    language: "en",
  });
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setValues((prev) => ({
      ...prev,
      email: "",
      password: "",
      companyName: "",
      contactPerson: "",
      country: "",
      communicationPlatform: platformOptions?.[0]?.value || "",
      communicationHandle: "",
      appWebsiteLink: "",
      dailyActiveUsers: "",
      monthlyRevenue: "",
      message: "",
      language: "en",
    }));
    setError("");
    setSuccess("");
  }, [open, platformOptions]);

  useEffect(() => {
    let cancelled = false;
    async function loadCountries() {
      try {
        const res = await fetch("https://restcountries.com/v3.1/all?fields=name");
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        if (!cancelled && Array.isArray(data)) {
          const list = data
            .map((item) => item?.name?.common)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
          setCountries(list);
        }
      } catch {
        if (!cancelled) setCountries(["United States", "United Kingdom", "Germany", "France", "Bulgaria"]);
      }
    }
    loadCountries();
    return () => { cancelled = true; };
  }, []);

  if (!open) return null;

  const update = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!values.email || !values.password || !values.companyName || !values.contactPerson || !values.country) {
      setError(copy.missingFields || FALLBACK.missingFields);
      return;
    }

    try {
      setLoading(true);
      const payload = { ...values };
      const response = await authApi.register(payload);
      const result = response?.result || response;
      const token = result?.token ?? "";
      if (token) setAuthToken(token);
      const user = result?.user || null;
      setSuccess(copy.success || FALLBACK.success);
      onSuccess({ token, user, payload, needsEmailVerification: result?.needsEmailVerification });
    } catch (err) {
      const message =
        err?.data?.statusText ||
        err?.data?.statusDesc ||
        err?.message ||
        copy.genericError ||
        FALLBACK.genericError;
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="desktop-login-overlay" role="dialog" aria-modal="true" onMouseDown={handleBackdrop}>
      <div className="desktop-login-card desktop-register-card">
        <button
          type="button"
          className="desktop-login-close"
          aria-label="Close register"
          onClick={onClose}
        >
          ×
        </button>

        <p className="desktop-login-eyebrow">{copy.eyebrow}</p>
        <h2 className="desktop-login-title">{copy.title}</h2>
        <p className="desktop-login-subtitle">{copy.subtitle}</p>

        <form className="desktop-login-form desktop-register-form" onSubmit={handleSubmit}>
          <label className="desktop-login-field">
            <span>{copy.emailLabel}</span>
            <input
              type="email"
              name="email"
              placeholder={copy.emailPlaceholder}
              value={values.email}
              onChange={update}
              autoComplete="email"
              required
            />
          </label>

          <label className="desktop-login-field">
            <span>{copy.passwordLabel}</span>
            <input
              type="password"
              name="password"
              placeholder={copy.passwordPlaceholder}
              value={values.password}
              onChange={update}
              autoComplete="new-password"
              required
            />
          </label>

          <div className="desktop-register-grid">
            <label className="desktop-login-field">
              <span>{copy.companyLabel}</span>
              <input
                type="text"
                name="companyName"
                placeholder={copy.companyPlaceholder}
                value={values.companyName}
                onChange={update}
                required
              />
            </label>

            <label className="desktop-login-field">
              <span>{copy.contactLabel}</span>
              <input
                type="text"
                name="contactPerson"
                placeholder={copy.contactPlaceholder}
                value={values.contactPerson}
                onChange={update}
                required
              />
            </label>
          </div>

          <div className="desktop-register-grid">
            <label className="desktop-login-field">
              <span>{copy.countryLabel}</span>
              <div className="register-select-wrap">
                <select
                  name="country"
                  value={values.country}
                  onChange={update}
                  required
                >
                  <option value="" disabled>{copy.countryPlaceholder}</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
            </label>

            <label className="desktop-login-field">
              <span>{copy.languageLabel}</span>
              <div className="register-select-wrap">
                <select
                  name="language"
                  value={values.language}
                  onChange={update}
                >
                  {LANG_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </label>
          </div>

          <div className="desktop-register-grid">
            <label className="desktop-login-field">
              <span>{copy.commPlatformLabel}</span>
              <div className="register-select-wrap">
                <select
                  name="communicationPlatform"
                  value={values.communicationPlatform}
                  onChange={update}
                >
                  {platformOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </label>

            <label className="desktop-login-field">
              <span>{copy.commHandleLabel}</span>
              <input
                type="text"
                name="communicationHandle"
                placeholder={copy.commHandlePlaceholder}
                value={values.communicationHandle}
                onChange={update}
              />
            </label>
          </div>

          <label className="desktop-login-field">
            <span>{copy.appLabel}</span>
            <input
              type="text"
              name="appWebsiteLink"
              placeholder={copy.appPlaceholder}
              value={values.appWebsiteLink}
              onChange={update}
            />
          </label>

          <div className="desktop-register-grid">
            <label className="desktop-login-field">
              <span>{copy.dauLabel}</span>
              <input
                type="text"
                name="dailyActiveUsers"
                placeholder={copy.dauPlaceholder}
                value={values.dailyActiveUsers}
                onChange={update}
              />
            </label>

            <label className="desktop-login-field">
              <span>{copy.revenueLabel}</span>
              <input
                type="text"
                name="monthlyRevenue"
                placeholder={copy.revenuePlaceholder}
                value={values.monthlyRevenue}
                onChange={update}
              />
            </label>
          </div>

          <label className="desktop-login-field">
            <span>{copy.messageLabel}</span>
            <textarea
              name="message"
              placeholder={copy.messagePlaceholder}
              rows={4}
              value={values.message}
              onChange={update}
            />
          </label>

          {(error || success) && (
            <div className={`desktop-login-status ${error ? "is-error" : "is-success"}`}>
              {error || success}
            </div>
          )}

          <button type="submit" className="desktop-login-submit" disabled={loading}>
            <span>{loading ? "..." : (copy.submit || FALLBACK.submit).toUpperCase()}</span>
          </button>
        </form>
      </div>
    </div>
  );
}
